import axios, { AxiosError } from "axios";
import { AuthTypeCheckInputType, AuthenticationInputType, PatientTEFCAAuthInfoDto, } from "healthex-common/auth.dtos";
import { err, voidResult } from "healthex-common/result";
import { apiConfig, authenticatedApiConfig, generateURL, retryTooManyRequests } from "./BaseApiService";
import { plainToInstance } from "class-transformer";
export class AuthService {
    constructor(environment) {
        this.environment = environment;
    }
    async generateTokenByPassword(email, password, organizationId) {
        return await retryTooManyRequests(async () => {
            var _a, _b, _c;
            try {
                const response = await axios.post(`${this.environment.API_BASE_URL}/auth/token`, { type: AuthenticationInputType.PASSWORD, email, password, organizationId }, { ...apiConfig(), withCredentials: true });
                return response.data;
            }
            catch (e) {
                if (e instanceof AxiosError && ((_a = e.response) === null || _a === void 0 ? void 0 : _a.status) === 401 && ((_b = e.response) === null || _b === void 0 ? void 0 : _b.data.error)) {
                    return err((_c = e.response) === null || _c === void 0 ? void 0 : _c.data.error);
                }
                throw e;
            }
        });
    }
    async getOrganizationsForEmail(email, redirectURL) {
        const response = await axios.post(`${this.environment.API_BASE_URL}/auth/organizations-for-email`, { email, redirectURL }, apiConfig());
        return response.data.organizations;
    }
    async refreshAccessToken(_userType, unVerifiedUserId, lastActivityAt) {
        var _a;
        try {
            const response = await axios.post(`${this.environment.API_BASE_URL}/auth/token`, { type: "REFRESH_TOKEN", lastActivityAt, unVerifiedUserId }, { ...apiConfig(), withCredentials: true });
            return response.data;
        }
        catch (e) {
            if (e instanceof AxiosError && ((_a = e.response) === null || _a === void 0 ? void 0 : _a.status) === 401) {
                return null;
            }
            throw e;
        }
    }
    async serverLogout(_userType, voluntary) {
        await axios.post(`${this.environment.API_BASE_URL}/auth/logout`, { voluntary }, { ...apiConfig(), withCredentials: true });
    }
    async getAuthType(emailOrOrganizationId, redirectURL = window.location.href) {
        const response = await axios.post(`${this.environment.API_BASE_URL}/auth/type`, {
            type: "email" in emailOrOrganizationId ? AuthTypeCheckInputType.EMAIL : AuthTypeCheckInputType.ORGANIZATION,
            ...emailOrOrganizationId,
            redirectURL,
        }, apiConfig());
        return response.data;
    }
    async resetPassword(password, token) {
        var _a, _b, _c;
        try {
            await axios.put(`${this.environment.API_BASE_URL}/auth/reset_password`, { password }, await authenticatedApiConfig({ token }));
            return voidResult();
        }
        catch (e) {
            if (e instanceof AxiosError) {
                if (e instanceof AxiosError && ((_a = e.response) === null || _a === void 0 ? void 0 : _a.status) === 401 && ((_b = e.response) === null || _b === void 0 ? void 0 : _b.data.error)) {
                    return err((_c = e.response) === null || _c === void 0 ? void 0 : _c.data.error);
                }
            }
            throw e;
        }
    }
    async changePassword(input) {
        var _a, _b, _c;
        try {
            await axios.put(`${this.environment.API_BASE_URL}/auth/change_password`, input, apiConfig());
            return voidResult();
        }
        catch (e) {
            if (e instanceof AxiosError) {
                if (e instanceof AxiosError && ((_a = e.response) === null || _a === void 0 ? void 0 : _a.status) === 401 && ((_b = e.response) === null || _b === void 0 ? void 0 : _b.data.error)) {
                    return err((_c = e.response) === null || _c === void 0 ? void 0 : _c.data.error);
                }
            }
            throw e;
        }
    }
    async getShortLivedTokenForAppLaunch(authenticationProvider, token) {
        const response = await axios.post(`${this.environment.API_BASE_URL}/auth/token/short-lived`, { token }, await authenticatedApiConfig(authenticationProvider));
        return response.data;
    }
    async generateTokenFromShortLivedToken(token) {
        return await retryTooManyRequests(async () => {
            var _a, _b, _c;
            try {
                const response = await axios.post(`${this.environment.API_BASE_URL}/auth/token`, { type: AuthenticationInputType.EXISTING_TOKEN, token }, { ...apiConfig(), withCredentials: true });
                return response.data;
            }
            catch (e) {
                if (e instanceof AxiosError && ((_a = e.response) === null || _a === void 0 ? void 0 : _a.status) === 401 && ((_b = e.response) === null || _b === void 0 ? void 0 : _b.data.error)) {
                    return err((_c = e.response) === null || _c === void 0 ? void 0 : _c.data.error);
                }
                throw e;
            }
        });
    }
    async generateTokenByPatientId(authenticationProvider, patientId, organizationName) {
        var _a, _b, _c;
        try {
            const response = await axios.post(`${this.environment.API_BASE_URL}/auth/patientbyid/token`, { patientId, organizationName }, await authenticatedApiConfig(authenticationProvider));
            return response.data;
        }
        catch (e) {
            if (e instanceof AxiosError && ((_a = e.response) === null || _a === void 0 ? void 0 : _a.status) === 401 && ((_b = e.response) === null || _b === void 0 ? void 0 : _b.data.error)) {
                return err((_c = e.response) === null || _c === void 0 ? void 0 : _c.data.error);
            }
            throw e;
        }
    }
    async generateTokenByPatientIdWithIAL2ForTesting(authenticationProvider, patientId, organizationId) {
        var _a, _b, _c;
        try {
            const response = await axios.post(`${this.environment.API_BASE_URL}/auth/token/testing`, { patientId, organizationId, type: AuthenticationInputType.CLEAR_IAL2 }, await authenticatedApiConfig(authenticationProvider));
            return response.data;
        }
        catch (e) {
            if (e instanceof AxiosError && ((_a = e.response) === null || _a === void 0 ? void 0 : _a.status) === 401 && ((_b = e.response) === null || _b === void 0 ? void 0 : _b.data.error)) {
                return err((_c = e.response) === null || _c === void 0 ? void 0 : _c.data.error);
            }
            throw e;
        }
    }
    async generateTokenByExistingToken(token) {
        return await retryTooManyRequests(async () => {
            var _a, _b, _c;
            try {
                const response = await axios.post(`${this.environment.API_BASE_URL}/auth/token`, { type: AuthenticationInputType.EXISTING_TOKEN, token }, { ...apiConfig(), withCredentials: true });
                return response.data;
            }
            catch (e) {
                if (e instanceof AxiosError && ((_a = e.response) === null || _a === void 0 ? void 0 : _a.status) === 401 && ((_b = e.response) === null || _b === void 0 ? void 0 : _b.data.error)) {
                    return err((_c = e.response) === null || _c === void 0 ? void 0 : _c.data.error);
                }
                throw e;
            }
        });
    }
    async generateTokenByClearGFIDWithIAL2(authCode, redirectUri, organizationId, authTarget, sandboxMode, heapGlobalEventProperties, linkingToken, existingAuthToken) {
        var _a, _b, _c;
        try {
            const body = {
                type: AuthenticationInputType.CLEAR_IAL2,
                authCode,
                redirectUri,
                organizationId,
                authTarget,
                sandboxMode,
                heapGlobalEventProperties,
                linkingToken,
                existingAuthToken,
            };
            const response = await axios.post(`${this.environment.API_BASE_URL}/auth/token`, body, {
                ...apiConfig(),
                withCredentials: true,
            });
            return response.data;
        }
        catch (e) {
            if (e instanceof AxiosError && ((_a = e.response) === null || _a === void 0 ? void 0 : _a.status) === 401 && ((_b = e.response) === null || _b === void 0 ? void 0 : _b.data.error)) {
                return err((_c = e.response) === null || _c === void 0 ? void 0 : _c.data.error);
            }
            throw e;
        }
    }
    async linkAccount(linkingToken, authenticationProvider) {
        const body = { linkingToken };
        const response = await axios.post(`${this.environment.API_BASE_URL}/auth/link-account`, body, await authenticatedApiConfig(authenticationProvider));
        return response.data;
    }
    async sendPasswordResetEmail(email, organizationId) {
        const response = await axios.post(`${this.environment.API_BASE_URL}/auth/forgot_password`, { email, organizationId }, apiConfig());
        return response.status;
    }
    async getAuthenticatedUserWithDetails(authenticationProvider) {
        const response = await axios.get(`${this.environment.API_BASE_URL}/auth/user-details`, await authenticatedApiConfig(authenticationProvider));
        return response.data;
    }
    async getPatientTEFCAAuthInfo(patientId, authenticationProvider) {
        const response = await axios.get(generateURL(this.environment.API_BASE_URL, "/patients/:patientId/tefca-auth-info", { patientId }), await authenticatedApiConfig(authenticationProvider));
        return plainToInstance(PatientTEFCAAuthInfoDto, response.data);
    }
    async switchAccount(input, authenticationProvider) {
        const response = await axios.post(`${this.environment.API_BASE_URL}/auth/switch-account`, input, {
            ...(await authenticatedApiConfig(authenticationProvider)),
            withCredentials: true,
        });
        return response.data;
    }
}
